import React from "react";
import { normalizePortugueseEncoding } from "../../utils/atlasText";
import { atlasColors } from "./atlasTheme";

export type ClinicalCardVariant = "default" | "warning" | "success" | "neutral";

type Props = {
  title: string;
  eyebrow?: string;
  items?: string[];
  description?: string;
  variant?: ClinicalCardVariant;
  children?: React.ReactNode;
  className?: string;
};

const normalizeList = (items?: string[]) =>
  (items || [])
    .map((item) => normalizePortugueseEncoding(String(item || "")).replace(/\s+/g, " ").trim())
    .filter(Boolean)
    .slice(0, 8);

export function ClinicalCard({
  title,
  eyebrow,
  items = [],
  description,
  variant = "default",
  children,
  className = "",
}: Props) {
  const normalizedItems = normalizeList(items);
  const normalizedEyebrow = eyebrow ? normalizePortugueseEncoding(eyebrow).replace(/\s+/g, " ").trim() : "";
  const normalizedTitle = normalizePortugueseEncoding(title).replace(/\s+/g, " ").trim();
  const showEyebrow =
    Boolean(normalizedEyebrow) &&
    normalizedEyebrow.toLowerCase() !== normalizedTitle.toLowerCase();
  const isActive = variant === "warning" || variant === "success";

  return (
    <section
      className={`rounded-[20px] border p-[18px] shadow-[0_18px_50px_rgba(0,0,0,0.22)] ${className}`}
      style={{
        background: atlasColors.bgCardPremium,
        borderColor: isActive ? atlasColors.borderActive : atlasColors.borderMuted,
        boxShadow: isActive
          ? "0 20px 60px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(34, 211, 238, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.07)"
          : "0 18px 45px rgba(0, 0, 0, 0.28), inset 0 1px 0 rgba(255, 255, 255, 0.06)",
      }}
    >
      {showEyebrow ? (
        <div className="mb-1 text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: atlasColors.accent }}>
          {normalizedEyebrow}
        </div>
      ) : null}
      <div className="text-[12px] font-bold uppercase tracking-[0.18em]" style={{ color: atlasColors.accent }}>
        {normalizedTitle}
      </div>
      {description ? (
        <div className="mt-2 text-[14px] leading-[1.55]" style={{ color: atlasColors.textSecondary }}>
          {normalizePortugueseEncoding(description)}
        </div>
      ) : null}

      {children ? (
        <div className="mt-3">{children}</div>
      ) : normalizedItems.length > 0 ? (
        <ul className="mt-3 space-y-2">
          {normalizedItems.map((item, index) => (
            <li key={`${item}-${index}`} className="flex items-start gap-2 text-[14px] leading-[1.55]" style={{ color: atlasColors.textPrimary }}>
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: atlasColors.accent }} />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      ) : (
        <div className="mt-3 text-[14px] leading-[1.55]" style={{ color: atlasColors.textSecondary }}>
          {normalizePortugueseEncoding("Sem informação estruturada.")}
        </div>
      )}
    </section>
  );
}

export default ClinicalCard;
